import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useConfigStore } from "@/stores/config";
import { getReportFooter, getReportHeader } from "@/reports/ReportUtil";
import { styles } from "@/reports/reportStyles";
import { clone } from "@/util/clone";
import { MAINTENANCE_CANDIDATE_TYPE } from "@/constants/InspectionConstants";
import { getFormattedDateStringNoTime } from "@/composables/dateField.js";
import { convertToInt } from "@/composables/util";

function setMaintenanceDocDefinition(structure, includeCompleted) {
  const configStore = useConfigStore();
  let proposed = loadCandidates(MAINTENANCE_CANDIDATE_TYPE.PROPOSED, structure);
  let completed = loadCandidates(MAINTENANCE_CANDIDATE_TYPE.COMPLETED, structure);
  proposed?.sort((a, b) => {
    if (convertToInt(a?.agencyPriority) == convertToInt(b?.agencyPriority)) {
      return new Date(a?.workRecDate) - new Date(b?.workRecDate);
    } else {
      return convertToInt(a?.agencyPriority) - convertToInt(b?.agencyPriority);
    }
  });
  completed?.sort((a, b) => {
    return convertToInt(a?.agencyPriority) - convertToInt(b?.agencyPriority);
  });
  const proposedTable = getProposedCandidates(configStore, getFormattedDateStringNoTime, proposed);
  const completedTable = getCompletedCandidates(configStore, getFormattedDateStringNoTime, completed);
  let docDefinition = {
    header: function () {
      return getReportHeader("Maintenance  Page", structure);
    },
    footer: function (currentPage, pageCount) {
      return getReportFooter(currentPage, pageCount);
    },
    pageMargins: [20, 70, 20, 50],
    content: [
      { text: "", pageHeaderText: "Maintenance  Page" },
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Proposed Candidates",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 5],
      },
      {
        table: {
          widths: ["27%", "30%", "10%", "7%", "10%", "16%"],
          headerRows: 1,
          body: proposedTable,
          margin: [0, 0, 0, 5],
        },
      },
    ],
    styles: styles,
    defaultStyle: {
      fontSize: 10,
    },
  };
  if (includeCompleted) {
    docDefinition.content.push(
      {
        table: {
          headerRows: 0,
          widths: ["100%"],
          body: [
            [
              {
                text: "Completed Candidates",
                fillColor: "#ebebeb",
                alignment: "center",
                style: ["sectionHeader"],
              },
            ],
          ],
        },
        layout: "noBorders",
        margin: [0, 5, 0, 5],
      },
      {
        table: {
          widths: ["25%", "25%", "8%", "8%", "8%", "14%", "12%"],
          headerRows: 1,
          body: completedTable,
          margin: [0, 0, 0, 5],
        },
      },
    );
  }
  return docDefinition;
}
const loadCandidates = (candidateType, structure) => {
  if (candidateType == MAINTENANCE_CANDIDATE_TYPE.PROPOSED) {
    return getCandidates(MAINTENANCE_CANDIDATE_TYPE.PROPOSED, structure);
  } else {
    return getCandidates(MAINTENANCE_CANDIDATE_TYPE.COMPLETED, structure);
  }
};
const getStatuses = (candidateType) => {
  if (candidateType == MAINTENANCE_CANDIDATE_TYPE.PROPOSED) {
    return ["0", "1", "2", "3", "4", "D"];
  } else {
    return ["5", "6", "7"];
  }
};
const getUOM = (item, configStore) => {
  if (item?.actKind === "1") {
    return "";
  } else {
    return configStore.getFlexAction(item?.actCode)?.uom;
  }
};
const getCandidates = (candidateType, structure) => {
  let proposed = [];
  let completed = [];
  let cloneWCand = clone(structure?.inspectionWcands);
  if (!cloneWCand?.[0]) {
    //remove null values from array(if any)
    cloneWCand = cloneWCand?.filter((a) => !!a);
  }
  let statuses = getStatuses(candidateType);

  for (const c of cloneWCand ?? []) {
    if (statuses?.includes(c?.agencyStatus)) {
      const userWcand = structure?.inspectionWcands?.find((element) => element?.wcId == c?.wcId);
      c.location = userWcand?.location;
      c.deferredActionDesc = userWcand?.deferredActionDesc;

      if (candidateType == MAINTENANCE_CANDIDATE_TYPE.COMPLETED) {
        c.completedDate = userWcand?.completedDate;
      }
      c.readOnly = true;
      c.isAdd = false;
      if (candidateType == MAINTENANCE_CANDIDATE_TYPE.PROPOSED) {
        proposed.push(c);
      } else {
        completed.push(c);
      }
    }
  }
  if (candidateType == MAINTENANCE_CANDIDATE_TYPE.PROPOSED) {
    return proposed;
  } else {
    return completed;
  }
};
function getProposedCandidates(configStore, getFormattedDateStringNoTime, proposed) {
  let proposedTable = [];
  proposedTable.push([
    {
      text: "M.01\n  Candidate ID\n",
      style: ["tableHeader"],
    },
    {
      text: "M.02\n Action\n",
      style: ["tableHeader"],
    },
    {
      text: "M.04\n Est\n Qty",
      style: ["tableHeader"],
    },
    {
      text: "\n UOM\n",
      style: ["tableHeader"],
    },
    {
      text: "M.05\n Priority\n",
      style: ["tableHeader"],
    },
    {
      text: "M.07\nInitial\nRecom'd Date",
      style: ["tableHeader"],
    },
  ]);
  for (const element of proposed ?? []) {
    proposedTable.push([
      {
        text: element?.wcId,
        style: ["tableBody"],
      },
      {
        text:
          element?.actKind == "1"
            ? configStore.getBridgeActionTitle(element?.actCode)
            : configStore.getFlexActionTitle(element?.actCode),
        style: ["tableBody"],
      },
      {
        text: element?.estimQuantity,
        style: ["tableBody"],
      },
      {
        text: getUOM(element, configStore),
        style: ["tableBody"],
      },
      {
        text: element?.agencyPriority,
        style: ["tableBody"],
      },
      {
        text: getFormattedDateStringNoTime(element?.workRecDate),
        style: ["tableBody"],
      },
    ],
    [
      {
        colSpan: 6,
        stack: [
          {
            columns: [
              {
                width: "20%",
                stack: [
                  {
                    text: "M.03 Status: ",
                    bold: true,
                    width: "20%",
                    margin: [0, 5, 0, 0],
                  },
                  {
                    text: "M.10 Target Year: ",
                    bold: true,
                    width: "20%",
                    margin: [0, 5, 0, 0],
                  },
                  {
                    text: "M.11 Work Assign: ",
                    bold: true,
                    width: "20%",
                    margin: [0, 5, 0, 0],
                  },
                ],
              },

              {
                width: "25%",
                stack: [
                  {
                    text: configStore.getReferenceValue(REFERENCE_TABLE.AGENCY_STATUS, element?.agencyStatus),
                    margin: [0, 5, 0, 0],
                  },
                  {
                    text: element?.targetYear,
                    margin: [0, 5, 0, 0],
                  },
                  {
                    text: configStore.getReferenceValue(REFERENCE_TABLE.WORK_ASSIGNMENT, element?.workAssignment),
                    margin: [0, 5, 0, 0],
                  },
                ],
              },
              {
                width: "55%",
                stack: [
                  {
                    text: "M.17 Location: ",
                    bold: true,
                    margin: [0, 5, 0, 0],
                  },
                  {
                    text: element?.location,
                  },
                ],
              },
            ],
          },
          {
            colSpan: 6,
            text: "M.18 Work Candidate Notes: ",
            bold: true,
            margin: [0, 5, 0, 0],
          },
          {
            colSpan: 6,
            text: element?.notes,
          },
        ],
      },
    ]);
  }
  return proposedTable;
}
function getCompletedCandidates(configStore, getFormattedDateStringNoTime, completed) {
  let completedTable = [];
  completedTable.push([
    {
      text: "M.01\n  Candidate ID\n",
      style: ["tableHeader"],
    },
    {
      text: "M.02\n Action\n",
      style: ["tableHeader"],
    },
    {
      text: "M.04\n Est\n Qty",
      style: ["tableHeader"],
    },
    {
      text: "\n UOM\n",
      style: ["tableHeader"],
    },
    {
      text: "M.05\n Priority\n",
      style: ["tableHeader"],
    },
    {
      text: "M.07\nInitial\nRecom'd Date",
      style: ["tableHeader"],
    },
    {
      text: "M.16\n Completed Date\n",
      style: ["tableHeader"],
    },
  ]);
  for (const element of completed ?? []) {
    completedTable.push([
      {
        text: element?.wcId,
        style: ["tableBody"],
      },
      {
        text:
          element?.actKind == "1"
            ? configStore.getBridgeActionTitle(element?.actCode)
            : configStore.getFlexActionTitle(element?.actCode),
        style: ["tableBody"],
      },
      {
        text: element?.ESTIMQUANTITY,
        style: ["tableBody"],
      },
      {
        text: getUOM(element, configStore),
        style: ["tableBody"],
      },
      {
        text: element?.agencyPriority,
        style: ["tableBody"],
      },
      {
        text: getFormattedDateStringNoTime(element?.workRecDate),
        style: ["tableBody"],
      },
      {
        text: getFormattedDateStringNoTime(element?.completedDate),
        style: ["tableBody"],
      },
    ],
    [
      {
        colSpan: 7,
        columns: [
          {
            width: "20%",
            stack: [
              {
                text: "M.03 Status: ",
                bold: true,
                width: "20%",
                margin: [0, 5, 0, 0],
              },
              {
                text: "M.10 Target Year: ",
                bold: true,
                width: "20%",
                margin: [0, 5, 0, 0],
              },
              {
                text: "M.11 Work Assign: ",
                bold: true,
                width: "20%",
                margin: [0, 5, 0, 0],
              },
            ],
          },

          {
            width: "25%",
            stack: [
              {
                text: configStore.getReferenceValue(REFERENCE_TABLE.AGENCY_STATUS, element?.agencyStatus),
                margin: [0, 5, 0, 0],
              },
              {
                text: element?.targetYear,
                margin: [0, 5, 0, 0],
              },
              {
                text: configStore.getReferenceValue(REFERENCE_TABLE.WORK_ASSIGNMENT, element?.workAssignment),
                margin: [0, 5, 0, 0],
              },
            ],
          },
          {
            width: "55%",
            stack: [
              {
                text: "M.17 Location: ",
                bold: true,
                margin: [0, 5, 0, 0],
              },
              {
                text: element?.location,
              },
              {
                text: "M.18 Work Candidate Notes: ",
                bold: true,
                margin: [0, 5, 0, 0],
              },
              {
                text: element?.notes,
              },
            ],
          },
        ],
      },
    ]);
  }
  return completedTable;
}

export { setMaintenanceDocDefinition };
